import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type {
  OrgCapabilities,
  ProjectCapabilities,
} from "@/app/capabilities/types";
import { useResourceScope } from "@/lib/resource-context";
import {
  orgCapabilitiesQueryOptions,
  orgPermissionsQueryOptions,
  projectCapabilitiesQueryOptions,
  projectPermissionsQueryOptions,
} from "./get-capabilities";

const selectOrgAuthzEnabled = (caps: OrgCapabilities) => caps.authz.enabled;

/** Suspense variant: use when capabilities are required to render. */
const useSuspenseGetCapabilities = () => {
  const scope = useResourceScope();
  return useSuspenseQuery(projectCapabilitiesQueryOptions(scope));
};

/** Non-suspense variant: use for capability-gated UI with explicit loading states. */
export const useGetCapabilities = () => {
  const scope = useResourceScope();
  return useQuery(projectCapabilitiesQueryOptions(scope));
};

/** Suspense selector helper for a single capability flag (e.g. billing). */
export const useSuspenseCapability = <K extends keyof ProjectCapabilities>(
  key: K
): ProjectCapabilities[K] => {
  const { data } = useSuspenseGetCapabilities();
  return data[key];
};

/** Suspense project-permission checker: `can(permission)` for gating UI. */
export function useProjectPermissions() {
  const scope = useResourceScope();
  const canProject = useSuspenseQuery(
    projectPermissionsQueryOptions(scope)
  ).data;

  return useMemo(() => ({ canProject }), [canProject]);
}

/**
 * Suspense org-permission checker: `canOrg(permission)` for gating org-level UI.
 * Fails closed for non-admins (the org capabilities endpoint is Org-Admin-only); see
 * `orgCapabilitiesQueryOptions`.
 */
export function useOrgPermissions() {
  const { organizationId } = useResourceScope();
  const canOrg = useSuspenseQuery(
    orgPermissionsQueryOptions(organizationId)
  ).data;
  // Org-only surfaces exist only when operator-RBAC is enabled; `canOrg` is
  // allow-all when it's off, so callers gating an org-only nav/route also need
  // this flag to stay in step with the route guard (which 404s when disabled).
  const orgAuthzEnabled = useSuspenseQuery({
    ...orgCapabilitiesQueryOptions(organizationId),
    select: selectOrgAuthzEnabled,
  }).data;

  return useMemo(
    () => ({ canOrg, orgAuthzEnabled }),
    [canOrg, orgAuthzEnabled]
  );
}
